import { Checkbox } from '@carbon/react';
import { useAtom } from 'jotai';
import { MapUserConfigAtom } from '../types';
import style from '../map.module.scss';
import { CollapsibleSection } from '../collapsible-section';
import pipelineStyle from './pipeline.module.scss';
import React from 'react';

// Import PNG files
import gasOperatingPng from 'images/pipelines/gas-operating.png';
import gasPlannedPng from 'images/pipelines/gas-planned.png';
import gasUnderConstructionPng from 'images/pipelines/gas-under-construction.png';
import oilOperatingPng from 'images/pipelines/oil-operating.png';
import oilPlannedPng from 'images/pipelines/oil-planned.png';
import oilUnderConstructionPng from 'images/pipelines/oil-under-construction.png';
import productOperatingPng from 'images/pipelines/product-operating.png';
import productPlannedPng from 'images/pipelines/product-planned.png';
import productUnderConstructionPng from 'images/pipelines/product-under-construction.png';
import unknownOperatingPng from 'images/pipelines/unknown-operating.png';
import unknownPlannedPng from 'images/pipelines/unknown-planned.png';
import unknownUnderConstructionPng from 'images/pipelines/unknown-under-construction.png';

type PipelineCategoryKey = 'gas' | 'oil' | 'product' | 'unknown';

// SVG mapping for CSS custom properties
const imageMap = {
  'pipeline-gas-operating': gasOperatingPng,
  'pipeline-gas-plannedOther': gasPlannedPng,
  'pipeline-gas-underConstruction': gasUnderConstructionPng,
  'pipeline-oil-operating': oilOperatingPng,
  'pipeline-oil-plannedOther': oilPlannedPng,
  'pipeline-oil-underConstruction': oilUnderConstructionPng,
  'pipeline-product-operating': productOperatingPng,
  'pipeline-product-construction': productUnderConstructionPng,
  'pipeline-product-plannedOther': productPlannedPng,
  'pipeline-unknown-operating': unknownOperatingPng,
  'pipeline-unknown-plannedOther': unknownPlannedPng,
  'pipeline-unknown-underConstruction': unknownUnderConstructionPng,
};

export const PipelineSelector = ({
  mapConfigAtom,
}: {
  mapConfigAtom: MapUserConfigAtom;
}) => {
  const [mapConfig, setMapConfig] = useAtom(mapConfigAtom);

  // Define our categories and status filters
  const categories = {
    gas: {
      label: 'Gas',
      key: 'gas' as const,
    },
    oil: {
      label: 'Oil',
      key: 'oil' as const,
    },
    product: {
      label: 'Product',
      key: 'product' as const,
    },
    unknown: {
      label: 'Unknown',
      key: 'unknown' as const,
    },
  };

  // Status filters that will appear under each category with their colors
  const statusFilters = [
    { key: 'operating', label: 'Operating' },
    { key: 'underConstruction', label: 'Under Construction' },
    { key: 'plannedOther', label: 'Planned/Other' },
  ];

  // Product-specific status filters
  const productStatusFilters = [
    { key: 'operating', label: 'Operating' },
    { key: 'construction', label: 'Under Construction' },
    { key: 'plannedOther', label: 'Planned/Other' },
  ];

  const statusColorMap = {
    gasOperating: 'pipelineGasOperating',
    gasUnderConstruction: 'pipelineGasUnderConstruction',
    gasPlannedOther: 'pipelineGasPlanned',
    oilOperating: 'pipelineOilOperating',
    oilUnderConstruction: 'pipelineOilUnderConstruction',
    oilPlannedOther: 'pipelineOilPlanned',
    productOperating: 'pipelineProductOperating',
    productConstruction: 'pipelineProductUnderConstruction',
    productPlannedOther: 'pipelineProductPlanned',
    unknownOperating: 'pipelineUnknownOperating',
    unknownUnderConstruction: 'pipelineUnknownUnderConstruction',
    unknownPlannedOther: 'pipelineUnknownPlanned',
  };

  // Helper function to determine checkbox state for categories
  const getCategoryCheckState = (
    categoryKey: PipelineCategoryKey
  ): { checked: boolean; indeterminate: boolean } => {
    const isProduct = categoryKey === 'product';
    const filters = isProduct ? productStatusFilters : statusFilters;

    // Get all child filter keys for this category
    const childFilters = filters.map((status) => {
      return status.key;
    });

    // Check if all child filters are checked
    const allChildrenChecked = childFilters.every((filter) => {
      const optionKey = `${categoryKey}${
        filter.charAt(0).toUpperCase() + filter.slice(1)
      }` as keyof typeof mapConfig.pipelines.options;
      return mapConfig.pipelines?.options?.[optionKey] === true;
    });

    // Check if some but not all child filters are checked
    const someChildrenChecked = childFilters.some((filter) => {
      const optionKey = `${categoryKey}${
        filter.charAt(0).toUpperCase() + filter.slice(1)
      }` as keyof typeof mapConfig.pipelines.options;
      return mapConfig.pipelines?.options?.[optionKey] === true;
    });

    return {
      checked: allChildrenChecked,
      indeterminate: !allChildrenChecked && someChildrenChecked,
    };
  };

  const handleCategoryChange = (
    categoryKey: PipelineCategoryKey,
    checked: boolean
  ) =>
    setMapConfig((current) => {
      const isProduct = categoryKey === 'product';
      const filters = isProduct ? productStatusFilters : statusFilters;

      // Create a new config object with flat structure
      const newConfig = {
        ...current,
        pipelines: {
          ...current.pipelines,
          checked: current.pipelines?.checked || false,
          options: {
            ...current.pipelines?.options,
          },
        },
      };

      // If checking a category, check all its child filters
      if (checked) {
        // Check all child filters for this category
        filters.forEach((status) => {
          const optionKey = `${categoryKey}${
            status.key.charAt(0).toUpperCase() + status.key.slice(1)
          }` as keyof typeof newConfig.pipelines.options;
          newConfig.pipelines.options[optionKey] = true;
        });
      } else {
        // If unchecking a category, also uncheck all its child filters
        filters.forEach((status) => {
          const optionKey = `${categoryKey}${
            status.key.charAt(0).toUpperCase() + status.key.slice(1)
          }` as keyof typeof newConfig.pipelines.options;
          newConfig.pipelines.options[optionKey] = false;
        });
      }

      // Check if any option is checked to determine overall layer visibility
      const anyOptionChecked = Object.values(newConfig.pipelines.options).some(
        (value) => value
      );
      newConfig.pipelines.checked = anyOptionChecked;

      return newConfig;
    });

  const handleStatusChange = (
    categoryKey: PipelineCategoryKey,
    statusKey: string,
    checked: boolean
  ) =>
    setMapConfig((current) => {
      const newConfig = {
        ...current,
        pipelines: {
          ...current.pipelines,
          checked: current.pipelines?.checked || false,
          options: {
            ...current.pipelines?.options,
          },
        },
      };

      // Update the specific status
      const optionKey = `${categoryKey}${
        statusKey.charAt(0).toUpperCase() + statusKey.slice(1)
      }` as keyof typeof newConfig.pipelines.options;
      newConfig.pipelines.options[optionKey] = checked;

      // Check if any option is checked to determine overall layer visibility
      const anyOptionChecked = Object.values(newConfig.pipelines.options).some(
        (value) => value
      );
      newConfig.pipelines.checked = anyOptionChecked;

      return newConfig;
    });

  return (
    <div className={style.layersSelector}>
      {Object.values(categories).map((category) => {
        const isProduct = category.key === 'product';
        const filters = isProduct ? productStatusFilters : statusFilters;
        const categoryState = getCategoryCheckState(category.key);

        return (
          <CollapsibleSection
            key={category.key}
            id={`pipeline-${category.key}`}
            title={category.label}
            className={style.pipelinecategorySection}
            checked={categoryState.checked}
            indeterminate={categoryState.indeterminate}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              handleCategoryChange(category.key, checked)
            }
            defaultOpen={true}
          >
            <div
              className={`${pipelineStyle.groupChecks} ${pipelineStyle.pipelineCategorySection}`}
            >
              {filters.map((status) => {
                const pipelineKey = `pipeline-${category.key}-${status.key}`;
                const imageUrl = imageMap[pipelineKey as keyof typeof imageMap];
                // Convert to camelCase for statusColorMap lookup
                const colorMapKey = `${category.key}${
                  status.key.charAt(0).toUpperCase() + status.key.slice(1)
                }` as keyof typeof statusColorMap;

                const isChecked = (() => {
                  const optionKey = `${category.key}${
                    status.key.charAt(0).toUpperCase() + status.key.slice(1)
                  }` as keyof typeof mapConfig.pipelines.options;
                  return mapConfig.pipelines?.options?.[optionKey] || false;
                })();

                const colors = {
                  bg: 'rgba(255, 255, 255, 0.25)',
                  border: 'rgb(255, 255, 255)',
                };

                return (
                  <div
                    key={status.key}
                    className={pipelineStyle[statusColorMap[colorMapKey]]}
                    style={
                      {
                        '--pipeline-icon': `url(${imageUrl})`,
                        '--pipeline-bg-color': colors.bg,
                        '--pipeline-border-color': colors.border,
                      } as React.CSSProperties
                    }
                  >
                    <div className={pipelineStyle.checkboxContainer}>
                      <Checkbox
                        id={`pipeline-${category.key}-${status.key}`}
                        labelText={status.label}
                        className={`${pipelineStyle.pipelineStatusOption} ${
                          pipelineStyle[
                            `pipeline-${category.key}-${status.key}`
                          ]
                        }`}
                        checked={isChecked}
                        onChange={(_evt, { checked }: { checked: boolean }) =>
                          handleStatusChange(category.key, status.key, checked)
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        );
      })}
    </div>
  );
};
